// Accessible "Load More" for ALFC collection pages.
// A real, focusable button loads the next page each time it's clicked, and can
// be clicked repeatedly to page through the whole collection. There is no
// scroll auto-loading — products only load when the shopper asks. Keyboard /
// screen-reader users get a fully working control and live announcements.
//
// Why a custom element: the collection grid is re-rendered (innerHTML wiped)
// whenever a shopper filters or sorts via facets.js. A custom element's
// connectedCallback fires on the fresh markup, so this re-initializes itself
// automatically and re-seeds its de-dup state from the new grid — no stale
// node references, no broken loader after filtering.
//
// The grid collapses "grouped" rod/reel collections into a single card, so the
// same grouped collection can span multiple paginated pages. The de-dup Sets
// below prevent a grouped card (or any product) from appearing twice as pages
// are appended.

class AlfcLoadMore extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.alfc-load-more__button');
    this.status = this.querySelector('.alfc-load-more__status');
    this.grid = document.getElementById('product-grid');

    // Track what's already on the page so we never append a duplicate.
    this.displayedCollections = new Set(); // grouped-rods / grouped-reels handles
    this.displayedProducts = new Set();    // individual product ids
    this.seedFromGrid();

    if (this.button) this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    event.preventDefault();
    this.loadMore();
  }

  seedFromGrid() {
    if (!this.grid) return;
    this.grid.querySelectorAll(':scope > li').forEach((item) => {
      const { collectionHandle, productId } = item.dataset;
      if (collectionHandle) this.displayedCollections.add(collectionHandle);
      if (productId) this.displayedProducts.add(productId);
    });
  }

  async loadMore() {
    if (this.loading || !this.dataset.nextUrl || !this.grid) return;
    this.loading = true;
    // Capture focus state BEFORE setBusy() disables the button — disabling a
    // focused element blurs it, which would otherwise defeat the focus handoff.
    const buttonHadFocus = !!this.button && document.activeElement === this.button;
    this.setBusy(true);

    try {
      // Section Rendering API: fetch only this section's markup, not the whole
      // page. The next-page URL already carries any active filter/sort params.
      const url = new URL(this.dataset.nextUrl, window.location.origin);
      if (this.dataset.sectionId) url.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = doc.querySelectorAll('#product-grid > li');
      const nextLoader = doc.querySelector('alfc-load-more');

      let added = 0;
      let firstAdded = null;
      incoming.forEach((item) => {
        const { collectionHandle, productId } = item.dataset;
        if (collectionHandle && this.displayedCollections.has(collectionHandle)) return;
        if (!collectionHandle && productId && this.displayedProducts.has(productId)) return;

        const appended = this.grid.appendChild(document.importNode(item, true));
        if (!firstAdded) firstAdded = appended;
        if (collectionHandle) this.displayedCollections.add(collectionHandle);
        if (productId) this.displayedProducts.add(productId);
        added += 1;
      });

      // Re-bind backorder handlers for the newly inserted cards.
      if (typeof addCartBackOrder === 'function') addCartBackOrder();

      this.announce(`${added} more product${added === 1 ? '' : 's'} loaded.`);

      if (nextLoader && nextLoader.dataset.nextUrl) {
        this.dataset.nextUrl = nextLoader.dataset.nextUrl;
      } else {
        this.finish(firstAdded, buttonHadFocus);
      }
    } catch (error) {
      console.error('Load more failed:', error);
      this.announce('Sorry — more products could not be loaded. Please try again.');
    } finally {
      this.loading = false;
      this.setBusy(false);
    }
  }

  finish(firstAdded, buttonHadFocus) {
    // No more pages: clear the URL (stops any further fetches).
    delete this.dataset.nextUrl;

    // If a keyboard user activated the button, don't strand their focus on the
    // <body> when we remove it — move it into the first newly loaded card.
    if (this.button) this.button.remove();

    if (buttonHadFocus && firstAdded) {
      const target = firstAdded.querySelector('a, button, [tabindex]') || firstAdded;
      if (!target.hasAttribute('tabindex') && target === firstAdded) target.setAttribute('tabindex', '-1');
      target.focus();
    }
  }

  setBusy(isBusy) {
    if (!this.button) return;
    this.button.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    this.button.disabled = isBusy;
    this.classList.toggle('alfc-load-more--loading', isBusy);
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('alfc-load-more')) {
  customElements.define('alfc-load-more', AlfcLoadMore);
}

document.addEventListener('DOMContentLoaded', function () {
  addCartBackOrder();
});

function addCartBackOrder() {
  const addToCartButtons = document.querySelectorAll("form[data-type='add-to-cart-form'] .quick-add__submit-back-order");

  addToCartButtons.forEach(function (addToCartBtn) {
    // Skip if already bound
    if (addToCartBtn.classList.contains("backorder-listener-added")) return;

    const parentLi = addToCartBtn.closest("li[data-inventory-quantity][data-inventory-policy]");
    if (!parentLi) return;

    const inventoryQuantity = parseInt(parentLi.getAttribute("data-inventory-quantity"), 10);
    const inventoryPolicy = parentLi.getAttribute("data-inventory-policy");

    if (isBackorderable(inventoryQuantity, inventoryPolicy)) {
     const form = addToCartBtn.closest("form");
    const hiddenElementBtn = form.querySelector(".quick-add__submit__hidden");
      addToCartBtn.addEventListener("click", function (e) {
        e.preventDefault();
        showBackorderPopup(() => {
          // Optionally submit the form after confirming
          const clickEvent = new Event("click");
          hiddenElementBtn.dispatchEvent(clickEvent);

        });
      });

      // Mark as bound
      addToCartBtn.classList.add("backorder-listener-added");
    }
  });
}

 function showBackorderPopup(onConfirm) {
      const modal = document.createElement("div");
      modal.id = "backorder-modal";
      modal.style.position = "fixed";
      modal.style.top = 0;
      modal.style.left = 0;
      modal.style.width = "100%";
      modal.style.height = "100%";
      modal.style.backgroundColor = "rgba(0, 0, 0, 0.6)";
      modal.style.zIndex = "9999";
      modal.style.display = "flex";
      modal.style.alignItems = "center";
      modal.style.justifyContent = "center";

      modal.innerHTML = `
        <div style="background:#fff;padding:30px;border-radius:10px;max-width:400px;text-align:center;">
          <h3 style="margin-bottom: 15px;">On Backorder</h3>
          <p>This item is currently on backorder. It will ship as soon as it's back in stock.</p>
          <button id="backorder-ok" style="margin-top:20px;padding:10px 20px;border:none;background:#000;color:#fff;border-radius:5px;cursor:pointer;">Okay</button>
        </div>
      `;

      document.body.appendChild(modal);

      document.getElementById("backorder-ok").addEventListener("click", () => {
        document.body.removeChild(modal);
        onConfirm();
      });
    }
